import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import { closeForm } from "../feature/formSlice";
import { callAPI } from "../feature/APISlice";
import { showMessage } from "../feature/navbarSlice";
import { fetchLookups } from "../feature/lookupSlice";

const BASE_URL = `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/admin/core-services`;

const getDefaultFormData = () => ({
  title: "",
  shortDescription: "",
  longDescription: "",
  orderNumber: "",

  bannerImage: null,
  bannerVideo: null,
  mobileBannerImage: null,
  mobileBannerVideo: null,

  relatedMedia: [],
  coreInitiatives: [],
  events: [],

  is_active: false,
});

export default function CoreServicesForm() {
  const dispatch = useDispatch();

  const { isOpen, editingId } = useSelector((s) => s.form);
  const { current, loading, error } = useSelector((s) => s.api);
  const { media, coreInitiatives, events } = useSelector((s) => s.lookups);

  const [formData, setFormData] = useState(getDefaultFormData());

  useEffect(() => {
    if (isOpen) dispatch(fetchLookups());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (editingId) {
      dispatch(callAPI({ method: "get", url: `${BASE_URL}/${editingId}` }));
    }
  }, [editingId, dispatch]);

  useEffect(() => {
    if (current && editingId) {
      setFormData({
        title: current.title ?? "",
        shortDescription: current.shortDescription ?? "",
        longDescription: current.longDescription ?? "",
        orderNumber: current.orderNumber ?? "",

        bannerImage: current.bannerImage?.rivid ?? null,
        bannerVideo: current.bannerVideo?.rivid ?? null,
        mobileBannerImage: current.mobileBannerImage?.rivid ?? null,
        mobileBannerVideo: current.mobileBannerVideo?.rivid ?? null,

        relatedMedia: current.relatedMedia?.map((m) => m.rivid) ?? [],
        coreInitiatives: current.coreInitiatives?.map((i) => i.id) ?? [],
        events: current.events?.map((e) => e.id) ?? [],

        is_active: current.is_active ?? false,
      });
    }

    if (!editingId) setFormData(getDefaultFormData());
  }, [current, editingId]);

  const mediaOptions = useMemo(
    () =>
      media.map((m) => ({
        value: m.rivid,
        label: `RIVID: ${m.rivid}`,
      })),
    [media]
  );

  const coreInitiativeOptions = useMemo(
    () =>
      coreInitiatives.map((i) => ({
        value: i.id,
        label: i.title,
      })),
    [coreInitiatives]
  );

  const eventOptions = useMemo(
    () =>
      events.map((e) => ({
        value: e.id,
        label: e.title,
      })),
    [events]
  );

  const handleChange = (name, value) =>
    setFormData((p) => ({ ...p, [name]: value }));

  const handleClose = () => {
    dispatch(closeForm());
    setFormData(getDefaultFormData());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingId ? "patch" : "post";
      const url = editingId ? `${BASE_URL}/${editingId}` : BASE_URL;
      const payload = {
        title: formData.title,
        shortDescription: formData.shortDescription,
        longDescription: formData.longDescription,
        orderNumber: formData.orderNumber
          ? Number(formData.orderNumber)
          : null,

        bannerImage: formData.bannerImage || null,
        bannerVideo: formData.bannerVideo || null,
        mobileBannerImage: formData.mobileBannerImage || null,
        mobileBannerVideo: formData.mobileBannerVideo || null,

        relatedMedia: Array.isArray(formData.relatedMedia)
          ? formData.relatedMedia
          : [],

        coreInitiatives: Array.isArray(formData.coreInitiatives)
          ? formData.coreInitiatives
          : [],

        events: Array.isArray(formData.events)
          ? formData.events
          : [],

        is_active: formData.is_active,
      };

      await dispatch(callAPI({ method, url, data: payload })).unwrap();

      dispatch(
        showMessage({
          type: "success",
          text: `Core Service ${editingId ? "updated" : "created"} successfully`,
        })
      );

      handleClose();
    } catch (err) {
      dispatch(
        showMessage({
          type: "error",
          text: err || "Something went wrong",
        })
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-form-overlay">
      <div className="admin-form-wrapper" onClick={(e) => e.stopPropagation()}>
        <button className="admin-form-close" onClick={handleClose}>
          ✕
        </button>

        <h2>{editingId ? "Edit Core Service" : "Add Core Service"}</h2>

        <form className="admin-contact-form" onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </div>

          <div className="admin-form-group">
            <label>Short Description</label>
            <textarea
              rows={2}
              value={formData.shortDescription}
              onChange={(e) =>
                handleChange("shortDescription", e.target.value)
              }
            />
          </div>

          <div className="admin-form-group">
            <label>Long Description</label>
            <textarea
              rows={4}
              value={formData.longDescription}
              onChange={(e) =>
                handleChange("longDescription", e.target.value)
              }
            />
          </div>

          <div className="admin-form-group">
            <label>Order Number</label>
            <input
              type="number"
              value={formData.orderNumber}
              onChange={(e) =>
                handleChange("orderNumber", e.target.value)
              }
            />
          </div>

          {[
            ["bannerImage", "Desktop Banner Image"],
            ["bannerVideo", "Desktop Banner Video"],
            ["mobileBannerImage", "Mobile Banner Image"],
            ["mobileBannerVideo", "Mobile Banner Video"],
          ].map(([key, label]) => (
            <div className="admin-form-group" key={key}>
              <label>{label} (Media RIVID)</label>
              <Select
                options={mediaOptions}
                value={
                  mediaOptions.find(
                    (o) => o.value === Number(formData[key])
                  ) || null
                }
                onChange={(o) => handleChange(key, o?.value || null)}
                isClearable
              />
            </div>
          ))}

          <div className="admin-form-group">
            <label>Related Media</label>
            <Select
              options={mediaOptions}
              isMulti
              value={mediaOptions.filter((o) =>
                formData.relatedMedia.includes(o.value)
              )}
              onChange={(opts) =>
                handleChange(
                  "relatedMedia",
                  opts ? opts.map((o) => o.value) : []
                )
              }
            />
          </div>

          <div className="admin-form-group">
            <label>Core Initiatives</label>
            <Select
              isMulti
              options={coreInitiativeOptions}
              value={coreInitiativeOptions.filter((o) =>
                formData.coreInitiatives.includes(o.value)
              )}
              onChange={(opts) =>
                handleChange(
                  "coreInitiatives",
                  opts ? opts.map((o) => o.value) : []
                )
              }
            />
          </div>

          <div className="admin-form-group">
            <label>Events</label>
            <Select
              isMulti
              options={eventOptions}
              value={eventOptions.filter((o) =>
                formData.events.includes(o.value)
              )}
              onChange={(opts) =>
                handleChange(
                  "events",
                  opts ? opts.map((o) => o.value) : []
                )
              }
            />
          </div>

          <div className="admin-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  handleChange("is_active", e.target.checked)
                }
              />{" "}
              Active
            </label>
          </div>

          <div className="admin-button-row">
            <button
              type="button"
              className="admin-btn outline"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn filled"
              disabled={loading}
            >
              {loading ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>

          {error && <p className="error-text">{error}</p>}
        </form>
      </div>
    </div>
  );
}

